/**
 * The model behind the radar series — VV/VH beside the optical strip.
 *
 * Nothing here is derived from the verdict and nothing here feeds it. The backend collects
 * the series AFTER the answer is settled (app/pipeline/runner.py `observe_radar`); this
 * module only decides where to put a dot.
 *
 * The measured finding it must not contradict (docs/EXP_RADAR_VALUE.md): radar refinement
 * never changed which fields were found and made the DATING worse — the optical bracket
 * contained the labelled harvest 40/40, the refined one 26-30/40. So no state, no class and
 * no "confirms" is computed from these numbers anywhere in the UI.
 */

import type { RadarObservation, Run } from "../api/types";
import { isFiniteNumber, parseDate } from "./format";

export interface RadarPoint {
  key: string;
  date: string;
  track: string;
  vv: number | null;
  vh: number | null;
  /** 0..1 along the time axis of the window. */
  position: number;
}

export interface RadarSeriesModel {
  windowStart: string;
  windowEnd: string;
  points: RadarPoint[];
  tracks: string[];
  accepted: number;
  seen: number;
  /** Axis bounds in dB, already padded — the component draws, it does not decide scale. */
  min: number;
  max: number;
}

/** Padding around the observed range so the extreme points are not on the frame. */
const AXIS_PAD_DB = 1;
/** Minimum axis span: a flat series must not be magnified into a dramatic wiggle. */
const MIN_AXIS_SPAN_DB = 4;

const observationOf = (run: Run | null): RadarObservation | null => {
  const observation = run?.payload?.radar_observation ?? null;
  return observation && Array.isArray(observation.series) ? observation : null;
};

/**
 * Was the series collected at all? Distinguishes "collected, and it is empty" from
 * "never collected" — an empty window is a real answer over half of Russia, and a run with
 * RADAR_OBSERVE off is a different sentence.
 */
export const radarWasObserved = (run: Run | null): boolean => run?.payload?.radar_observed === true;

/** The server's own reason, when there is one. The UI never parses it. */
export const radarSkipReason = (run: Run | null): string | null =>
  run?.payload?.radar_observe_skip_reason ?? null;

/**
 * Does this run's answer REST on radar? Then "показан для контекста" would be a lie, and
 * the block says so instead.
 */
export const radarAnsweredThisRun = (run: Run | null): boolean =>
  run?.payload?.answered_by_radar === true;

/**
 * Should the radar block exist at all?
 *
 * Only when there is a series. Since 2026-08-03 the series is bought only by a run whose
 * optics went blind, so on almost every run there is nothing — and a block that explains
 * why there is nothing is worse than no block: it puts the word "радар" on a screen where
 * radar played no part, and an absence rendered as a notice reads as a fault. When optics
 * sufficed the user must see no radar block, no radar mention, nothing.
 */
export const radarBlockApplies = (run: Run | null): boolean =>
  run?.payload?.radar_observed === true;

export function radarSeriesFromRun(run: Run | null): RadarSeriesModel | null {
  const observation = observationOf(run);
  if (observation === null) return null;

  const start = parseDate(observation.window_start);
  const end = parseDate(observation.window_end);
  if (start === null || end === null) return null;
  const span = Math.max(end.getTime() - start.getTime(), 1);

  const values: number[] = [];
  const points: RadarPoint[] = [];
  for (const row of observation.series) {
    const day = parseDate(row.date ?? null);
    if (day === null) continue;
    const vv = isFiniteNumber(row.vv_db) ? row.vv_db : null;
    const vh = isFiniteNumber(row.vh_db) ? row.vh_db : null;
    if (vv === null && vh === null) continue;
    if (vv !== null) values.push(vv);
    if (vh !== null) values.push(vh);
    const track = row.track_id ?? "";
    points.push({
      key: `${row.date}-${track}`,
      date: row.date as string,
      track,
      vv,
      vh,
      position: Math.min(1, Math.max(0, (day.getTime() - start.getTime()) / span)),
    });
  }

  points.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));

  let min = values.length > 0 ? Math.min(...values) - AXIS_PAD_DB : -25;
  let max = values.length > 0 ? Math.max(...values) + AXIS_PAD_DB : 0;
  if (max - min < MIN_AXIS_SPAN_DB) {
    const middle = (max + min) / 2;
    min = middle - MIN_AXIS_SPAN_DB / 2;
    max = middle + MIN_AXIS_SPAN_DB / 2;
  }

  return {
    windowStart: observation.window_start,
    windowEnd: observation.window_end,
    points,
    tracks: [...(observation.tracks ?? [])],
    accepted: observation.acquisitions_accepted ?? points.length,
    seen: observation.acquisitions_seen ?? points.length,
    min,
    max,
  };
}

export interface RadarEvent {
  fromPosition: number;
  toPosition: number;
  from: string;
  to: string;
  spanDays: number;
}

/**
 * The answer's own interval, placed on the radar window — and ONLY when the answer is the
 * radar's. This is the single chart a dated radar answer rests on, and it used to draw no
 * dates at all: two labels at the edges of the axis. Drawing the interval on a run the
 * optics answered would be worse than useless: it would hint at corroboration the
 * measurement does not give (docs/EXP_RADAR_VALUE.md).
 */
export const radarEventOf = (run: Run | null, model: RadarSeriesModel): RadarEvent | null => {
  if (!radarAnsweredThisRun(run)) return null;
  const from = run?.result?.event_from ?? null;
  const to = run?.result?.event_to ?? null;
  const start = parseDate(model.windowStart);
  const end = parseDate(model.windowEnd);
  const fromDate = parseDate(from);
  const toDate = parseDate(to);
  if (!from || !to || !start || !end || !fromDate || !toDate) return null;

  const span = Math.max(end.getTime() - start.getTime(), 1);
  const place = (day: Date): number =>
    Math.min(1, Math.max(0, (day.getTime() - start.getTime()) / span));
  return {
    from,
    to,
    fromPosition: place(fromDate),
    toPosition: place(toDate),
    spanDays: Math.max(0, Math.round((toDate.getTime() - fromDate.getTime()) / 86_400_000)),
  };
};

/**
 * Horizontal gridlines every 2 dB. The axis used to carry exactly two labels, both printed
 * with `num(value, 0)` — so an axis ending at -22.4 was labelled "-22", the same class of
 * error the strip's NDVI grid had already been fixed for once.
 */
export const decibelGrid = (model: RadarSeriesModel, stepDb = 2): number[] => {
  const lines: number[] = [];
  const first = Math.ceil(model.min / stepDb) * stepDb;
  for (let value = first; value <= model.max; value += stepDb) lines.push(value);
  return lines;
};

/**
 * One track's points, in time order. Tracks are drawn apart because a VV value from a
 * different relative orbit is a different look angle, not the next reading of the same
 * instrument — joining them would draw a step that is pure geometry.
 */
export const pointsByTrack = (model: RadarSeriesModel): [string, RadarPoint[]][] => {
  const groups = new Map<string, RadarPoint[]>();
  for (const point of model.points) {
    const bucket = groups.get(point.track);
    if (bucket) bucket.push(point);
    else groups.set(point.track, [point]);
  }
  return [...groups.entries()].sort((a, b) => a[0].localeCompare(b[0]));
};
